import React, { useMemo, useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageTitle from "../components/PageTitle";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCart } from "../context/CartContext";
import { useOffers } from "../utils/useOffers";
import { useAuth } from "../context/AuthContext";
import { toggleFavoriteProduct } from "../utils/wishlistApi";
import { useLanguage } from "../context/LanguageContext";
import { createSlug } from "../utils/slug";
import { getProductLink } from "../utils/productLink";
import { usePageMeta } from "../utils/usePageMeta";
import { normalizeSearchQuery } from "../utils/searchApi";

function ShopDefaultPage() {
  const queryClient = useQueryClient();
  const { addItem, open, items: cartItems } = useCart();
  const { offers } = useOffers();
  const { token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const offerSlug = searchParams.get("offer");
  const legacyOfferId = searchParams.get("offer_id"); // Backward compatibility
  const categoryId = searchParams.get("category_id");
  const searchQuery = searchParams.get("search");
  const page = Number(searchParams.get("page") || 0);
  const limit = Number(searchParams.get("limit") || 20);

  // Track offer ID from slug for language change fallback
  const offerIdFromSlugRef = React.useRef(null);

  // Find offer by slug or legacy offer_id
  const currentOffer = useMemo(() => {
    if (!offers || offers.length === 0) return null;

    if (offerSlug) {
      // First try to find by slug
      let offer = offers.find((offer) => {
        const slug = createSlug(offer.name);
        return slug === offerSlug;
      });

      // If not found by slug, try to find by stored offer ID (when language changes)
      // This handles the case where offer name changes with language
      if (!offer && offerIdFromSlugRef.current) {
        offer = offers.find(
          (offer) => String(offer.id) === String(offerIdFromSlugRef.current)
        );
      }

      // If still not found, try legacy offer_id
      if (!offer && legacyOfferId) {
        offer = offers.find(
          (offer) => String(offer.id) === String(legacyOfferId)
        );
      }

      // Store offer ID for future language changes
      if (offer) {
        offerIdFromSlugRef.current = String(offer.id);
      }

      return offer;
    } else if (legacyOfferId) {
      // Find by legacy offer_id (backward compatibility)
      const offer = offers.find(
        (offer) => String(offer.id) === String(legacyOfferId)
      );
      if (offer) {
        offerIdFromSlugRef.current = String(offer.id);
      }
      return offer;
    }

    return null;
  }, [offers, offerSlug, legacyOfferId]);

  // Get offer_id immediately - prioritize legacyOfferId from URL (no waiting)
  // This allows API to start fetching immediately when offer_id is in URL
  const offerId = legacyOfferId
    ? String(legacyOfferId)
    : currentOffer
    ? String(currentOffer.id)
    : null;

  // Redirect legacy offer_id URLs to include both slug and offer_id
  // Keep offer_id for immediate API calls, add slug for SEO
  useEffect(() => {
    if (legacyOfferId && currentOffer && !offerSlug) {
      const slug = createSlug(currentOffer.name);
      const newParams = new URLSearchParams(searchParams);
      // Keep offer_id, just add the slug
      newParams.set("offer", slug);
      navigate(`/shop/default?${newParams.toString()}`, { replace: true });
    }
  }, [legacyOfferId, currentOffer, offerSlug, searchParams, navigate]);

  // Update slug in URL when language changes and offer name changes
  useEffect(() => {
    if (currentOffer && offerSlug) {
      const newSlug = createSlug(currentOffer.name);
      // Only update if the slug has changed (e.g., due to language change)
      if (newSlug !== offerSlug) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("offer", newSlug);
        navigate(`/shop/default?${newParams.toString()}`, { replace: true });
      }
    }
  }, [currentOffer, offerSlug, searchParams, navigate, language]);
  const [layout, setLayout] = useState(() => {
    // Set default layout based on screen size
    if (typeof window !== "undefined") {
      return window.innerWidth >= 768 ? "grid-4" : "grid-2";
    }
    return "grid-4"; // fallback for SSR
  });
  const [userSelectedLayout, setUserSelectedLayout] = useState(null);
  const [sortValue, setSortValue] = useState("Sorting");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    availability: null,
    brand: null,
    color: null,
    size: null,
  });

  const {
    data: offerProductsResponse,
    isLoading: isLoadingOfferProducts,
    isError: isErrorOfferProducts,
  } = useQuery({
    queryKey: ["offerProducts", offerId, page, limit, language],
    enabled: !!offerId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      let url = `https://erp-elsalab.odoo.com/api/cs/products-grouped-brand?offer_id=${encodeURIComponent(
        offerId
      )}&groupby=offer&page=${encodeURIComponent(
        page
      )}&limit=${encodeURIComponent(limit)}`;
      if (language === "ar") {
        url += "&lang=ar";
      }
      const res = await fetch(url, {
        headers: { Accept: "application/json; charset=utf-8" },
      });
      if (!res.ok) throw new Error("Failed to load offer products");
      const buffer = await res.arrayBuffer();
      const text = new TextDecoder("utf-8").decode(buffer);
      return JSON.parse(text);
    },
  });

  const {
    data: categoryProductsResponse,
    isLoading: isLoadingCategoryProducts,
    isError: isErrorCategoryProducts,
  } = useQuery({
    queryKey: ["categoryProducts", categoryId, page, limit, language],
    enabled: !!categoryId && !offerId,
    queryFn: async () => {
      let url = `https://erp-elsalab.odoo.com/api/cs/products-grouped-brand?category_id=${encodeURIComponent(
        categoryId
      )}&page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`;
      if (language === "ar") {
        url += "&lang=ar";
      }
      const res = await fetch(url, {
        headers: { Accept: "application/json; charset=utf-8" },
      });
      if (!res.ok) throw new Error("Failed to load category products");
      const buffer = await res.arrayBuffer();
      const text = new TextDecoder("utf-8").decode(buffer);
      return JSON.parse(text);
    },
  });

  const {
    data: searchProductsResponse,
    isLoading: isLoadingSearchProducts,
    isError: isErrorSearchProducts,
  } = useQuery({
    queryKey: ["searchProducts", searchQuery, page, limit, language],
    enabled: !!searchQuery && !offerId && !categoryId,
    queryFn: async () => {
      const normalizedSearch = normalizeSearchQuery(searchQuery);
      let url = `https://erp-elsalab.odoo.com/api/cs/products-grouped-brand?search_query=${encodeURIComponent(
        normalizedSearch
      )}&groupby=search&page=${page}&limit=${limit}`;
      if (language === "ar") {
        url += "&lang=ar";
      }

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain; charset=UTF-8",
        },
        body: "",
      });

      if (!res.ok) {
        throw new Error(`Search failed with status ${res.status}`);
      }

      // Use the same decoding approach as mobile app
      const buffer = await res.arrayBuffer();
      const text = new TextDecoder("utf-8").decode(buffer);
      const data = JSON.parse(text);

      if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
        const group = data.data[0] || {};
        const items = Array.isArray(group.products) ? group.products : [];

        const products = items.map((p) => ({
          id: String(p.id || p.template_id || ""),
          name: p.name || "",
          title: p.name || "",
          price: typeof p.price === "number" ? p.price : null,
          list_price: typeof p.list_price === "number" ? p.list_price : null,
          image:
            p.image_url_1920 ||
            p.image_url ||
            (p.id ? `https://erp-elsalab.odoo.com/web/image/${p.id}` : ""),
          image_url_1920: p.image_url_1920 || null,
          image_url: p.image_url || null,
          availability:
            p.stock && p.stock[0] && p.stock[0].total_free > 0
              ? "In stock"
              : "Out of stock",
          brand: p.att_model_name || "",
          colors: p.att_color ? [p.att_color] : [],
          sizes: p.att_size && p.att_size !== "-" ? [p.att_size] : [],
          att_size: p.att_size && p.att_size !== "-" ? p.att_size : null,
          att_specifications: p.att_specifications || null,
          description: p.description || null,
          categ_name: p.categ_name || null,
          current_offer: p.current_offer || null,
          att_color: p.att_color || null,
          package_quantity: p.package_quantity || 1,
          stock: p.stock || [],
          category: p.category || null,
          is_favorite: p.is_favorite || false,
        }));

        return {
          products,
          total: data.count || 0,
          hasMore: items.length >= limit,
          brands: group.brands || [],
          subcategs: group.subcategs || [],
          types: group.types || [],
        };
      }

      return {
        products: [],
        total: 0,
        hasMore: false,
        brands: [],
        subcategs: [],
        types: [],
      };
    },
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache the results
  });

  const [localFavoriteState, setLocalFavoriteState] = useState({});
  // Track previous products to prevent showing dummy products during language changes
  const previousProductsRef = React.useRef([]);
  const previousQueryRef = React.useRef({ offerId, categoryId, searchQuery });

  // Clear previous products when navigating to a different page (offerId/categoryId/searchQuery changes)
  useEffect(() => {
    if (
      previousQueryRef.current.offerId !== offerId ||
      previousQueryRef.current.categoryId !== categoryId ||
      previousQueryRef.current.searchQuery !== searchQuery
    ) {
      previousProductsRef.current = [];
      previousQueryRef.current = { offerId, categoryId, searchQuery };
    }
  }, [offerId, categoryId, searchQuery]);

  const products = useMemo(() => {
    if (
      offerId &&
      offerProductsResponse &&
      Array.isArray(offerProductsResponse.data)
    ) {
      const group = offerProductsResponse.data[0] || {};
      const items = Array.isArray(group.products) ? group.products : [];
      const mappedProducts = items.map((p) => {
        const productId = String(p.id || p.template_id || "");
        return {
          id: productId,
          title: p.name || "",
          name: p.name || "",
          price: typeof p.price === "number" ? p.price : null,
          list_price: typeof p.list_price === "number" ? p.list_price : null,
          availability:
            p.stock && p.stock[0] && p.stock[0].total_free > 0
              ? "In stock"
              : "Out of stock",
          brand: p.att_model_name || "",
          colors: p.att_color ? [p.att_color] : [],
          sizes: p.att_size && p.att_size !== "-" ? [p.att_size] : [],
          att_size: p.att_size && p.att_size !== "-" ? p.att_size : null,
          att_specifications: p.att_specifications || null,
          description: p.description || null,
          categ_name: p.categ_name || null,
          current_offer: p.current_offer || null,
          att_color: p.att_color || null,
          image:
            p.image_url_1920 ||
            p.image_url ||
            (p.id ? `https://erp-elsalab.odoo.com/web/image/${p.id}` : ""),
          image_url_1920: p.image_url_1920 || null,
          image_url: p.image_url || null,
          is_favorite:
            localFavoriteState[productId] !== undefined
              ? localFavoriteState[productId]
              : p.is_favorite || false,
        };
      });
      // Save to ref for persistence during language changes
      previousProductsRef.current = mappedProducts;
      return mappedProducts;
    }
    if (
      categoryId &&
      categoryProductsResponse &&
      Array.isArray(categoryProductsResponse.data)
    ) {
      const group = categoryProductsResponse.data[0] || {};
      const items = Array.isArray(group.products) ? group.products : [];
      const mappedProducts = items.map((p) => {
        const productId = String(p.id || p.template_id || "");
        return {
          id: productId,
          title: p.name || "",
          name: p.name || "",
          price: typeof p.price === "number" ? p.price : null,
          list_price: typeof p.list_price === "number" ? p.list_price : null,
          availability:
            p.stock && p.stock[0] && p.stock[0].total_free > 0
              ? "In stock"
              : "Out of stock",
          brand: p.att_model_name || "",
          colors: p.att_color ? [p.att_color] : [],
          sizes: p.att_size && p.att_size !== "-" ? [p.att_size] : [],
          att_size: p.att_size && p.att_size !== "-" ? p.att_size : null,
          att_specifications: p.att_specifications || null,
          description: p.description || null,
          categ_name: p.categ_name || null,
          current_offer: p.current_offer || null,
          att_color: p.att_color || null,
          image:
            p.image_url_1920 ||
            p.image_url ||
            (p.id ? `https://erp-elsalab.odoo.com/web/image/${p.id}` : ""),
          image_url_1920: p.image_url_1920 || null,
          image_url: p.image_url || null,
          is_favorite:
            localFavoriteState[productId] !== undefined
              ? localFavoriteState[productId]
              : p.is_favorite || false,
        };
      });
      // Save to ref for persistence during language changes
      previousProductsRef.current = mappedProducts;
      return mappedProducts;
    }
    if (
      searchQuery &&
      searchProductsResponse &&
      Array.isArray(searchProductsResponse.products)
    ) {
      const mappedProducts = searchProductsResponse.products.map((p) => ({
        ...p,
        is_favorite:
          localFavoriteState[p.id] !== undefined
            ? localFavoriteState[p.id]
            : p.is_favorite || false,
      }));
      // Save to ref for persistence during language changes
      previousProductsRef.current = mappedProducts;
      return mappedProducts;
    }
    // If we're expecting real data (offer/category/search present) but it's not here yet
    // Return previous products if available, otherwise empty array
    if (offerId || categoryId || searchQuery) {
      // If we have previous products, keep showing them during language changes
      if (previousProductsRef.current.length > 0) {
        return previousProductsRef.current;
      }
      return [];
    }

    // No dummy products - return previous products if available, otherwise empty array
    return previousProductsRef.current.length > 0
      ? previousProductsRef.current
      : [];
  }, [
    offerId,
    offerProductsResponse,
    categoryId,
    categoryProductsResponse,
    searchQuery,
    searchProductsResponse,
    localFavoriteState,
  ]);

  // Prime the product-by-id cache for ProductDetail
  React.useEffect(() => {
    if (Array.isArray(products) && products.length > 0) {
      products.forEach((p) => {
        if (p && p.id) {
          queryClient.setQueryData(["productById", String(p.id)], p);
        }
      });
    }
  }, [products, queryClient]);

  // Handle window resize to adjust layout only if user hasn't manually selected one
  useEffect(() => {
    const handleResize = () => {
      // Only auto-adjust if user hasn't manually selected a layout
      if (!userSelectedLayout) {
        const newLayout = window.innerWidth >= 768 ? "grid-4" : "grid-2";
        setLayout(newLayout);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [userSelectedLayout]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const appliedFilters = useMemo(
    () =>
      Object.entries(filters).filter(([, v]) => v !== null && v !== undefined),
    [filters]
  );

  const filteredProducts = useMemo(() => {
    let list = products;
    if (filters.availability)
      list = list.filter((p) => p.availability === filters.availability);
    if (filters.brand) list = list.filter((p) => p.brand === filters.brand);
    if (filters.color)
      list = list.filter((p) => p.colors.includes(filters.color));
    if (filters.size) list = list.filter((p) => p.sizes.includes(filters.size));

    switch (sortValue) {
      case "Price, low to high":
        return [...list].sort((a, b) => a.price - b.price);
      case "Price, high to low":
        return [...list].sort((a, b) => b.price - a.price);
      default:
        return list;
    }
  }, [products, filters, sortValue]);

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));
  const removeFilter = (key) =>
    setFilters((prev) => ({ ...prev, [key]: null }));
  const removeAllFilters = () =>
    setFilters({ availability: null, brand: null, color: null, size: null });

  const layoutClass =
    layout === "list"
      ? "tf-list-layout"
      : `tf-grid-layout ${layout.replace("grid-", "tf-col-")}`;

  // Get offer name from offers list
  const offerName = useMemo(() => {
    if (currentOffer) {
      return (
        currentOffer.name ||
        (language === "ar" ? "عروض خاصة" : "Special Offers")
      );
    }
    if (offerId && Array.isArray(offers) && offers.length > 0) {
      const offer = offers.find((o) => String(o.id) === String(offerId));
      return (
        offer?.name || (language === "ar" ? "عروض خاصة" : "Special Offers")
      );
    }
    return language === "ar" ? "عروض خاصة" : "Special Offers";
  }, [currentOffer, offerId, offers, language]);

  // Set SEO meta tags using the hook
  const pageTitle = currentOffer
    ? `${currentOffer.name} - Abdelaziz Elsallab`
    : undefined;
  const pageDescription = currentOffer
    ? currentOffer.description ||
      (language === "ar"
        ? `تسوق من خلال ${currentOffer.name} - أفضل العروض والمنتجات من عبدالعزيز السلاب`
        : `Shop ${currentOffer.name} - Best offers and products from Abdelaziz Elsallab`)
    : undefined;
  const canonicalUrl = currentOffer
    ? `${window.location.origin}${
        language === "ar" ? "/ar" : ""
      }/shop/default?offer=${encodeURIComponent(
        createSlug(currentOffer.name)
      )}&offer_id=${currentOffer.id}`
    : undefined;

  usePageMeta({
    title: pageTitle,
    description: pageDescription,
    canonicalUrl: canonicalUrl,
  });

  return (
    <div className="page-shop">
      <style>
        {`
          @media only screen and (max-width: 575px) {
            /* Ensure wishlist button shows on mobile within product cards */
            .card-product .list-product-btn .wishlist {
              display: inline-flex !important;
              visibility: visible !important;
              opacity: 1 !important;
            }

            /* Make prices same size as desktop view on mobile */
            .card-product .price-area .current-price,
            .card-product .price-area .price.current-price,
            .card-product .card-product-info .price-area .current-price,
            .card-product .card-product-info .price-area .price.current-price,
            .card-product .card-product-info .price-area span.price.current-price {
              font-size: 1rem !important;
              line-height: 1rem !important;
            }
            
            .card-product .price-area .compare-at-price,
            .card-product .price-area .price.compare-at-price,
            .card-product .card-product-info .price-area .compare-at-price,
            .card-product .card-product-info .price-area .price.compare-at-price,
            .card-product .card-product-info .price-area span.price.compare-at-price {
              font-size: 0.9rem !important;
              line-height: 0.9rem !important;
            }

            /* Make product items smaller in width to display more products on mobile */
            .page-shop .tf-grid-layout.wrapper-shop {
              display: grid !important;
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              gap: 6px !important;
            }

            .page-shop .card-product.grid {
              padding: 6px !important;
              min-width: 0 !important;
              max-width: 100% !important;
            }

            .page-shop .card-product .card-product-wrapper {
              min-width: 0 !important;
              width: 100% !important;
            }

            .page-shop .card-product .product-img {
              width: 100% !important;
              min-width: 0 !important;
            }

            .page-shop .card-product .product-img img {
              width: 100% !important;
              height: auto !important;
              object-fit: contain !important;
            }

            .page-shop .card-product .title {
              font-size: 11px !important;
              line-height: 1.3 !important;
              margin-bottom: 3px !important;
              overflow: hidden !important;
              text-overflow: ellipsis !important;
              display: -webkit-box !important;
              -webkit-line-clamp: 2 !important;
              -webkit-box-orient: vertical !important;
            }

            .page-shop .card-product .card-product-info {
              padding: 4px 0 !important;
            }

            .page-shop .card-product .list-product-btn {
              gap: 4px !important;
            }

            .page-shop .card-product .list-product-btn .box-icon {
              width: 28px !important;
              height: 28px !important;
            }

            .page-shop .card-product .list-product-btn .icon {
              font-size: 12px !important;
            }
            
            /* Make color chips larger in mobile - override default circular shape completely */
            .page-shop .card-product .list-color-product {
              gap: 6px !important;
              margin-top: 6px !important;
              display: flex !important;
              flex-wrap: wrap !important;
            }
            
            .page-shop .card-product .list-color-item,
            .page-shop .card-product .color-swatch {
              width: auto !important;
              height: auto !important;
              min-width: 50px !important;
              min-height: 24px !important;
              max-width: none !important;
              padding: 3px 8px !important;
              border-radius: 12px !important;
              font-size: 10px !important;
              line-height: 1.2 !important;
              white-space: nowrap !important;
              overflow: visible !important;
              text-overflow: clip !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
              flex-shrink: 0 !important;
              border: solid 1px rgb(238, 238, 238) !important;
              background-color: transparent !important;
            }
            
            /* Hide or resize swatch-value if it exists (the color circle) */
            .page-shop .card-product .list-color-item .swatch-value {
              display: none !important;
            }
            
            /* Ensure text is visible and not clipped */
            .page-shop .card-product .list-color-item,
            .page-shop .card-product .color-swatch {
              text-align: center !important;
            }
            
            .page-shop .card-product .list-color-item *:not(.swatch-value),
            .page-shop .card-product .color-swatch *:not(.swatch-value) {
              white-space: nowrap !important;
              overflow: visible !important;
              text-overflow: clip !important;
              max-width: none !important;
            }
          }

          /* Make cart (bag) and favorite (heart) icons red within product cards */
          .card-product .list-product-btn .icon,
          .card-product .list-product-btn .icon:before {
            color: var(--primary) !important;
          }

          /* Cart icon bluish filled when product is in cart */
          .card-product .list-product-btn .box-icon.quick-add.product-in-cart {
            background-color: #0e5aa7 !important;
            color: #ffffff !important;
          }

          .card-product .list-product-btn .box-icon.quick-add.product-in-cart:hover {
            background-color: #0e5aa7 !important; /* Keep bluish background on hover */
            color: #ffffff !important;
          }

          .card-product .list-product-btn .box-icon.quick-add.product-in-cart .icon,
          .card-product .list-product-btn .box-icon.quick-add.product-in-cart .icon:before,
          .card-product .list-product-btn .box-icon.quick-add.product-in-cart:hover .icon,
          .card-product .list-product-btn .box-icon.quick-add.product-in-cart:hover .icon:before {
            color: #ffffff !important;
          }

          /* Add separation and elevation to product items */
          .card-product.grid,
          .card-product.list-layout {
            background: #fafafa; /* off-white */
            border: 1px solid rgba(14, 90, 167, 0.5);
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(14, 90, 167, 0.15);
            padding: 12px;
          }

          /* Product image framing with bluish border */
          .card-product .product-img {
            display: block;
            border-radius: 12px;
            background: #ffffff;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
            outline: 1px solid rgba(14, 90, 167, 0.4);
            transition: box-shadow 0.25s ease, transform 0.25s ease;
            position: relative;
          }

          .card-product .product-img img {
            border-radius: 10px;
          }

          .card-product:hover .product-img {
            transform: translateY(-2px);
            box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
          }

          /* Ensure inner content respects rounded corners */
          .card-product .card-product-wrapper,
          .card-product .card-product-info {
            border-radius: 10px;
            overflow: hidden;
          }

          /* Smaller price text */
          .card-product .price-area .current-price {
            font-weight: 600;
            font-size: 1.1rem;
          }
          .card-product .price-area .compare-at-price {
            font-weight: 400;
            font-size: 1rem;
            opacity: 0.85;
            position: relative;
            text-decoration: none !important;
          }
          
          .card-product .price-area .compare-at-price::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            width: 100%;
            height: 1px;
            background-color: #000;
            transform: rotate(-15deg);
          }

          /* Bluish color for page title */
          .tf-page-title.style-2-edit .heading {
            color: #0e5aa7 !important;
          }

          /* Bluish color for pagination/page switcher */
          .tf-pagination-list .pagination-link {
            color: #0e5aa7 !important;
            border-color: rgba(14, 90, 167, 0.3) !important;
          }

          .tf-pagination-list .pagination-link:hover {
            background-color: rgba(14, 90, 167, 0.1) !important;
            border-color: #0e5aa7 !important;
            color: #0e5aa7 !important;
          }

          .tf-pagination-list .active .pagination-link {
            background-color: #0e5aa7 !important;
            color: #ffffff !important;
            border-color: #0e5aa7 !important;
            box-shadow: 0 2px 8px rgba(14, 90, 167, 0.3) !important;
          }

          .tf-pagination-list .disabled .pagination-link {
            color: rgba(14, 90, 167, 0.3) !important;
            border-color: rgba(14, 90, 167, 0.2) !important;
            cursor: not-allowed !important;
          }
        `}
      </style>
      <Header />
      <PageTitle
        title={
          searchQuery
            ? language === "ar"
              ? `نتائج البحث عن "${searchQuery}"`
              : `Search Results for "${searchQuery}"`
            : offerId
            ? offerName
            : categoryId
            ? language === "ar"
              ? "منتجات الفئة"
              : "Category Products"
            : language === "ar"
            ? "وصل حديثاً"
            : "New Arrival"
        }
        subtitle={
          searchQuery
            ? language === "ar"
              ? `تم العثور على ${filteredProducts.length} منتج يطابق بحثك`
              : `Found ${filteredProducts.length} products matching your search`
            : offerId
            ? language === "ar"
              ? "تسوق من خلال عروضنا الخاصة"
              : "Shop through our special offers"
            : categoryId
            ? language === "ar"
              ? "تصفح المنتجات في هذه الفئة"
              : "Browse products in this category"
            : language === "ar"
            ? "تسوق من خلال أحدث مجموعاتنا من الأزياء"
            : "Shop through our latest selection of Fashion"
        }
      />

      <section className="flat-spacing-2">
        <div className="container">
          <div className="tf-shop-control grid-3 align-items-center">
            <div className="tf-control-filter">
              {/* <button
                className="tf-btn-filter"
                onClick={() => setIsFilterOpen(true)}
              >
                <span className="icon icon-filter"></span>
                <span className="text">Filter</span>
              </button> */}
            </div>
            <ul className="tf-control-layout d-flex justify-content-center">
              <li
                className={`tf-view-layout-switch sw-layout-list list-layout${
                  layout === "list" ? " active" : ""
                }`}
                onClick={() => {
                  setLayout("list");
                  setUserSelectedLayout("list");
                }}
              >
                <div className="item">
                  <span className="icon icon-list"></span>
                </div>
              </li>
              <li
                className={`tf-view-layout-switch sw-layout-2${
                  layout === "grid-2" ? " active" : ""
                }`}
                onClick={() => {
                  setLayout("grid-2");
                  setUserSelectedLayout("grid-2");
                }}
              >
                <div className="item">
                  <span className="icon icon-grid-2"></span>
                </div>
              </li>
              <li
                className={`tf-view-layout-switch sw-layout-3${
                  layout === "grid-3" ? " active" : ""
                }`}
                onClick={() => {
                  setLayout("grid-3");
                  setUserSelectedLayout("grid-3");
                }}
              >
                <div className="item">
                  <span className="icon icon-grid-3"></span>
                </div>
              </li>
              <li
                className={`tf-view-layout-switch sw-layout-4${
                  layout === "grid-4" ? " active" : ""
                }`}
                onClick={() => {
                  setLayout("grid-4");
                  setUserSelectedLayout("grid-4");
                }}
              >
                <div className="item">
                  <span className="icon icon-grid-4"></span>
                </div>
              </li>
              <li
                className={`tf-view-layout-switch sw-layout-5${
                  layout === "grid-5" ? " active" : ""
                }`}
                onClick={() => {
                  setLayout("grid-5");
                  setUserSelectedLayout("grid-5");
                }}
              >
                <div className="item">
                  <span className="icon icon-grid-5"></span>
                </div>
              </li>
              <li
                className={`tf-view-layout-switch sw-layout-6${
                  layout === "grid-6" ? " active" : ""
                }`}
                onClick={() => {
                  setLayout("grid-6");
                  setUserSelectedLayout("grid-6");
                }}
              >
                <div className="item">
                  <span className="icon icon-grid-6"></span>
                </div>
              </li>
            </ul>
            <div className="tf-control-sorting d-flex justify-content-end">
              <div className="tf-dropdown-sort">
                <div
                  className="btn-select"
                  onClick={(e) => {
                    const menu = e.currentTarget.nextElementSibling;
                    if (menu) menu.classList.toggle("show");
                  }}
                >
                  <span className="text-sort-value">
                    {language === "ar"
                      ? sortValue === "Sorting"
                        ? "الترتيب"
                        : sortValue === "Price, low to high"
                        ? "السعر: من الأقل إلى الأعلى"
                        : sortValue === "Price, high to low"
                        ? "السعر: من الأعلى إلى الأقل"
                        : sortValue
                      : sortValue}
                  </span>
                  <span className="icon icon-arrow-down"></span>
                </div>
                <div className="dropdown-menu">
                  {language === "ar"
                    ? [
                        "الترتيب",
                        "السعر: من الأقل إلى الأعلى",
                        "السعر: من الأعلى إلى الأقل",
                      ].map((opt, index) => {
                        const englishOpts = [
                          "Sorting",
                          "Price, low to high",
                          "Price, high to low",
                        ];
                        const optKey = englishOpts[index];
                        return (
                          <div
                            key={optKey}
                            className={`select-item${
                              sortValue === optKey ? " active" : ""
                            }`}
                            onClick={(e) => {
                              setSortValue(optKey);
                              const menu = e.currentTarget.parentElement;
                              if (menu) menu.classList.remove("show");
                            }}
                          >
                            <span className="text-value-item">{opt}</span>
                          </div>
                        );
                      })
                    : [
                        "Sorting",
                        "Price, low to high",
                        "Price, high to low",
                      ].map((opt) => (
                        <div
                          key={opt}
                          className={`select-item${
                            sortValue === opt ? " active" : ""
                          }`}
                          onClick={(e) => {
                            setSortValue(opt);
                            const menu = e.currentTarget.parentElement;
                            if (menu) menu.classList.remove("show");
                          }}
                        >
                          <span className="text-value-item">{opt}</span>
                        </div>
                      ))}
                </div>
              </div>
            </div>
          </div>

          <div className="wrapper-control-shop">
            <div className="meta-filter-shop">
              <div className="count-text">
                {offerId
                  ? isLoadingOfferProducts
                    ? language === "ar"
                      ? "جاري التحميل..."
                      : "Loading..."
                    : isErrorOfferProducts
                    ? language === "ar"
                      ? "فشل التحميل"
                      : "Failed to load"
                    : `${filteredProducts.length} ${
                        language === "ar" ? "منتج" : "products"
                      }`
                  : categoryId
                  ? isLoadingCategoryProducts
                    ? language === "ar"
                      ? "جاري التحميل..."
                      : "Loading..."
                    : isErrorCategoryProducts
                    ? language === "ar"
                      ? "فشل التحميل"
                      : "Failed to load"
                    : `${filteredProducts.length} ${
                        language === "ar" ? "منتج" : "products"
                      }`
                  : searchQuery
                  ? isLoadingSearchProducts
                    ? language === "ar"
                      ? "جاري التحميل..."
                      : "Loading..."
                    : isErrorSearchProducts
                    ? language === "ar"
                      ? "فشل التحميل"
                      : "Failed to load"
                    : `${filteredProducts.length} ${
                        language === "ar" ? "منتج" : "products"
                      }`
                  : `${filteredProducts.length} ${
                      language === "ar" ? "منتج" : "products"
                    }`}
              </div>
              <div className="count-text"></div>
              <div>
                {appliedFilters.length > 0 && (
                  <div id="applied-filters" className="d-flex gap-10 flex-wrap">
                    {appliedFilters.map(([key, value]) => {
                      const filterKeyLabels = {
                        availability:
                          language === "ar" ? "التوفر" : "availability",
                        brand: language === "ar" ? "العلامة التجارية" : "brand",
                        color: language === "ar" ? "اللون" : "color",
                        size: language === "ar" ? "المقاس" : "size",
                      };
                      const availabilityValueLabels = {
                        "In stock": language === "ar" ? "متوفر" : "In stock",
                        "Out of stock":
                          language === "ar" ? "غير متوفر" : "Out of stock",
                      };
                      const displayValue =
                        key === "availability" && availabilityValueLabels[value]
                          ? availabilityValueLabels[value]
                          : value;
                      return (
                        <span
                          key={key}
                          className="badge border-line-black d-flex align-items-center gap-6"
                        >
                          {filterKeyLabels[key] || key}: {displayValue}
                          <button
                            className="icon icon-close"
                            onClick={() => removeFilter(key)}
                            aria-label={
                              language === "ar"
                                ? "إزالة المرشح"
                                : "remove filter"
                            }
                          ></button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              {appliedFilters.length > 0 && (
                <button
                  id="remove-all"
                  className="remove-all-filters"
                  onClick={removeAllFilters}
                >
                  {language === "ar" ? "إزالة الكل" : "Remove All"}{" "}
                  <i className="icon icon-close"></i>
                </button>
              )}
            </div>

            {layout === "list" ? (
              <div className={`${layoutClass} wrapper-shop`} id="listLayout">
                {(offerId && isLoadingOfferProducts) ||
                (categoryId && isLoadingCategoryProducts) ||
                (searchQuery && isLoadingSearchProducts) ? (
                  <div style={{ padding: "20px 0" }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div
                        key={`shimmer-list-${i}`}
                        style={{
                          display: "flex",
                          gap: "20px",
                          marginBottom: "30px",
                          padding: "20px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "200px",
                            height: "250px",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "8px",
                            animation: "pulse 1.5s ease-in-out infinite",
                            animationDelay: `${i * 0.1}s`,
                            flexShrink: 0,
                          }}
                        ></div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              height: "24px",
                              width: "60%",
                              backgroundColor: "#e0e0e0",
                              borderRadius: "4px",
                              marginBottom: "15px",
                              animation: "pulse 1.5s ease-in-out infinite",
                              animationDelay: `${i * 0.1 + 0.1}s`,
                            }}
                          ></div>
                          <div
                            style={{
                              height: "20px",
                              width: "40%",
                              backgroundColor: "#e0e0e0",
                              borderRadius: "4px",
                              marginBottom: "20px",
                              animation: "pulse 1.5s ease-in-out infinite",
                              animationDelay: `${i * 0.1 + 0.2}s`,
                            }}
                          ></div>
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              marginBottom: "15px",
                            }}
                          >
                            {[1, 2, 3].map((j) => (
                              <div
                                key={j}
                                style={{
                                  width: "30px",
                                  height: "30px",
                                  backgroundColor: "#e0e0e0",
                                  borderRadius: "4px",
                                  animation: "pulse 1.5s ease-in-out infinite",
                                  animationDelay: `${i * 0.1 + 0.3 + j * 0.1}s`,
                                }}
                              ></div>
                            ))}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                            }}
                          >
                            {[1, 2, 3, 4].map((j) => (
                              <div
                                key={j}
                                style={{
                                  width: "40px",
                                  height: "24px",
                                  backgroundColor: "#e0e0e0",
                                  borderRadius: "4px",
                                  animation: "pulse 1.5s ease-in-out infinite",
                                  animationDelay: `${i * 0.1 + 0.4 + j * 0.1}s`,
                                }}
                              ></div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                    <style>{`
                      @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                      }
                    `}</style>
                  </div>
                ) : (
                  filteredProducts.map((p) => (
                    <div
                      key={p.id}
                      className="card-product list-layout"
                      data-availability={p.availability}
                      data-brand={p.brand}
                    >
                      <div className="card-product-wrapper">
                        <Link
                          to={getProductLink(p)}
                          state={{ product: p }}
                          className="product-img"
                        >
                          <img
                            className="lazyload img-product"
                            data-src={
                              p.image ||
                              `https://erp-elsalab.odoo.com/web/image/${p.id}`
                            }
                            src={
                              p.image ||
                              `https://erp-elsalab.odoo.com/web/image/${p.id}`
                            }
                            alt={p.title}
                          />
                          <img
                            className="lazyload img-hover"
                            data-src={
                              p.image ||
                              `https://erp-elsalab.odoo.com/web/image/${p.id}`
                            }
                            src={
                              p.image ||
                              `https://erp-elsalab.odoo.com/web/image/${p.id}`
                            }
                            alt={p.title}
                          />
                        </Link>
                        {p.list_price != null &&
                          p.price != null &&
                          Number(p.list_price) > 0 &&
                          Number(p.list_price) > Number(p.price) &&
                          Math.round(
                            ((Number(p.list_price) - Number(p.price)) /
                              Number(p.list_price)) *
                              100
                          ) > 0 && (
                            <div
                              className="on-sale-wrap"
                              style={{
                                position: "absolute",
                                top: 10,
                                left: 10,
                              }}
                            >
                              <div
                                className="on-sale-item"
                                style={{
                                  backgroundColor: "var(--primary)",
                                  color: "#fff",
                                }}
                              >
                                {Math.round(
                                  ((Number(p.list_price) - Number(p.price)) /
                                    Number(p.list_price)) *
                                    100
                                )}
                                % OFF
                              </div>
                            </div>
                          )}
                        <div className="list-product-btn absolute-2">
                          <a
                            href="#"
                            className={`box-icon bg_white quick-add tf-btn-loading ${
                              cartItems.some((item) => item.id === String(p.id))
                                ? "product-in-cart"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              addItem({ ...p, qty: 1 });
                              open();
                            }}
                          >
                            <span className="icon icon-bag"></span>
                            <span className="tooltip">
                              {language === "ar"
                                ? "أضف إلى السلة"
                                : "Add to cart"}
                            </span>
                          </a>
                          <button
                            type="button"
                            className={`box-icon bg_white wishlist btn-icon-action ${
                              p.is_favorite ? "active" : ""
                            }`}
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const currentFavorite = p.is_favorite || false;
                                const newFavoriteState = !currentFavorite;

                                // Optimistically update local state
                                setLocalFavoriteState((prev) => ({
                                  ...prev,
                                  [p.id]: newFavoriteState,
                                }));

                                await toggleFavoriteProduct(
                                  parseInt(p.id),
                                  newFavoriteState,
                                  null,
                                  p
                                );

                                console.log(
                                  `Product ${p.id} ${
                                    newFavoriteState
                                      ? "added to"
                                      : "removed from"
                                  } wishlist`
                                );
                              } catch (error) {
                                // Revert on error
                                setLocalFavoriteState((prev) => ({
                                  ...prev,
                                  [p.id]: p.is_favorite || false,
                                }));
                                console.error(
                                  "Failed to toggle favorite:",
                                  error
                                );
                                alert(
                                  error.message ||
                                    (language === "ar"
                                      ? "فشل تحديث قائمة الأمنيات"
                                      : "Failed to update wishlist")
                                );
                              }
                            }}
                          >
                            <span className="icon icon-heart"></span>
                            <span className="tooltip">
                              {p.is_favorite
                                ? language === "ar"
                                  ? "إزالة من قائمة الأمنيات"
                                  : "Remove from Wishlist"
                                : language === "ar"
                                ? "أضف إلى قائمة الأمنيات"
                                : "Add to Wishlist"}
                            </span>
                            <span className="icon icon-delete"></span>
                          </button>
                        </div>
                      </div>
                      <div className="card-product-info">
                        <Link
                          to={getProductLink(p)}
                          state={{ product: p }}
                          className="title link"
                        >
                          {p.title}
                        </Link>
                        {p.price != null && (
                          <div className="price-area">
                            <span
                              className="price current-price"
                              style={{ color: "var(--primary)" }}
                            >
                              EGP {Number(p.price).toFixed(1)}
                            </span>
                            {p.list_price != null &&
                              Number(p.list_price) > Number(p.price) && (
                                <span
                                  className="price compare-at-price"
                                  style={{
                                    color: "#000",
                                    marginLeft: 8,
                                  }}
                                >
                                  {Math.round(Number(p.list_price))}
                                </span>
                              )}
                          </div>
                        )}
                        <div className="list-color-product">
                          {p.colors.map((c) => (
                            <span
                              key={c}
                              className="list-color-item color-swatch"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                        <div className="size-list">
                          {p.sizes.map((s) => (
                            <span key={s} className="size-item">
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className={`${layoutClass} wrapper-shop`} id="gridLayout">
                {(offerId && isLoadingOfferProducts) ||
                (categoryId && isLoadingCategoryProducts) ||
                (searchQuery && isLoadingSearchProducts) ? (
                  <>
                    {Array.from(
                      {
                        length:
                          layout === "grid-2"
                            ? 4 // 2 columns × 2 rows
                            : layout === "grid-3"
                            ? 6 // 3 columns × 2 rows
                            : layout === "grid-4"
                            ? 8 // 4 columns × 2 rows
                            : layout === "grid-5"
                            ? 10 // 5 columns × 2 rows
                            : layout === "grid-6"
                            ? 12 // 6 columns × 2 rows
                            : 8, // default fallback
                      },
                      (_, i) => i + 1
                    ).map((i) => (
                      <div
                        key={`shimmer-grid-${i}`}
                        style={{
                          backgroundColor: "#fff",
                          borderRadius: "12px",
                          overflow: "hidden",
                          padding: "15px",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "1",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "8px",
                            marginBottom: "15px",
                            animation: "pulse 1.5s ease-in-out infinite",
                            animationDelay: `${i * 0.1}s`,
                          }}
                        ></div>
                        <div
                          style={{
                            height: "20px",
                            width: "80%",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "4px",
                            marginBottom: "10px",
                            animation: "pulse 1.5s ease-in-out infinite",
                            animationDelay: `${i * 0.1 + 0.1}s`,
                          }}
                        ></div>
                        <div
                          style={{
                            height: "18px",
                            width: "50%",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "4px",
                            marginBottom: "10px",
                            animation: "pulse 1.5s ease-in-out infinite",
                            animationDelay: `${i * 0.1 + 0.2}s`,
                          }}
                        ></div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            marginTop: "10px",
                          }}
                        >
                          {[1, 2].map((j) => (
                            <div
                              key={j}
                              style={{
                                width: "30px",
                                height: "30px",
                                backgroundColor: "#e0e0e0",
                                borderRadius: "4px",
                                animation: "pulse 1.5s ease-in-out infinite",
                                animationDelay: `${i * 0.1 + 0.3 + j * 0.1}s`,
                              }}
                            ></div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <style>{`
                      @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                      }
                    `}</style>
                  </>
                ) : (
                  filteredProducts.map((p) => (
                    <div
                      key={p.id}
                      className="card-product grid"
                      data-availability={p.availability}
                      data-brand={p.brand}
                    >
                      <div className="card-product-wrapper">
                        <Link
                          to={getProductLink(p)}
                          state={{ product: p }}
                          className="product-img"
                        >
                          <img
                            className="lazyload img-product"
                            data-src={
                              p.image ||
                              `https://erp-elsalab.odoo.com/web/image/${p.id}`
                            }
                            src={
                              p.image ||
                              `https://erp-elsalab.odoo.com/web/image/${p.id}`
                            }
                            alt={p.title}
                          />
                          <img
                            className="lazyload img-hover"
                            data-src={
                              p.image ||
                              `https://erp-elsalab.odoo.com/web/image/${p.id}`
                            }
                            src={
                              p.image ||
                              `https://erp-elsalab.odoo.com/web/image/${p.id}`
                            }
                            alt={p.title}
                          />
                        </Link>
                        {p.list_price != null &&
                          p.price != null &&
                          Number(p.list_price) > 0 &&
                          Number(p.list_price) > Number(p.price) &&
                          Math.round(
                            ((Number(p.list_price) - Number(p.price)) /
                              Number(p.list_price)) *
                              100
                          ) > 0 && (
                            <div
                              className="on-sale-wrap"
                              style={{
                                position: "absolute",
                                top: 10,
                                left: 10,
                              }}
                            >
                              <div
                                className="on-sale-item"
                                style={{
                                  backgroundColor: "var(--primary)",
                                  color: "#fff",
                                }}
                              >
                                {Math.round(
                                  ((Number(p.list_price) - Number(p.price)) /
                                    Number(p.list_price)) *
                                    100
                                )}
                                % OFF
                              </div>
                            </div>
                          )}
                        <div className="list-product-btn absolute-2">
                          <a
                            href="#"
                            className={`box-icon bg_white quick-add tf-btn-loading ${
                              cartItems.some((item) => item.id === String(p.id))
                                ? "product-in-cart"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              addItem({ ...p, qty: 1 });
                              open();
                            }}
                          >
                            <span className="icon icon-bag"></span>
                            <span className="tooltip">
                              {language === "ar"
                                ? "أضف إلى السلة"
                                : "Add to cart"}
                            </span>
                          </a>
                          <button
                            type="button"
                            className={`box-icon bg_white wishlist btn-icon-action ${
                              p.is_favorite ? "active" : ""
                            }`}
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const currentFavorite = p.is_favorite || false;
                                const newFavoriteState = !currentFavorite;

                                // Optimistically update local state
                                setLocalFavoriteState((prev) => ({
                                  ...prev,
                                  [p.id]: newFavoriteState,
                                }));

                                await toggleFavoriteProduct(
                                  parseInt(p.id),
                                  newFavoriteState,
                                  null,
                                  p
                                );

                                console.log(
                                  `Product ${p.id} ${
                                    newFavoriteState
                                      ? "added to"
                                      : "removed from"
                                  } wishlist`
                                );
                              } catch (error) {
                                // Revert on error
                                setLocalFavoriteState((prev) => ({
                                  ...prev,
                                  [p.id]: p.is_favorite || false,
                                }));
                                console.error(
                                  "Failed to toggle favorite:",
                                  error
                                );
                                alert(
                                  error.message ||
                                    (language === "ar"
                                      ? "فشل تحديث قائمة الأمنيات"
                                      : "Failed to update wishlist")
                                );
                              }
                            }}
                          >
                            <span className="icon icon-heart"></span>
                            <span className="tooltip">
                              {p.is_favorite
                                ? language === "ar"
                                  ? "إزالة من قائمة الأمنيات"
                                  : "Remove from Wishlist"
                                : language === "ar"
                                ? "أضف إلى قائمة الأمنيات"
                                : "Add to Wishlist"}
                            </span>
                            <span className="icon icon-delete"></span>
                          </button>
                        </div>
                      </div>
                      <div className="card-product-info">
                        <Link
                          to={getProductLink(p)}
                          state={{ product: p }}
                          className="title link"
                        >
                          {p.title}
                        </Link>
                        {p.price != null && (
                          <div className="price-area">
                            <span
                              className="price current-price"
                              style={{ color: "var(--primary)" }}
                            >
                              EGP {Number(p.price).toFixed(1)}
                            </span>
                            {p.list_price != null &&
                              Number(p.list_price) > Number(p.price) && (
                                <span
                                  className="price compare-at-price"
                                  style={{
                                    color: "#000",
                                    marginLeft: 8,
                                  }}
                                >
                                  {Math.round(Number(p.list_price))}
                                </span>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Pagination - show 5 pages at a time (same behavior as ShopCollection) */}
            {(() => {
              // Calculate which 5 pages to show (e.g., if page=0-4 show 1-5, if page=5-9 show 6-10)
              const startPage = Math.floor(page / 5) * 5;
              const pagesToShow = [1, 2, 3, 4, 5].map((i) => startPage + i);

              return (
                <ul
                  className="wg-pagination tf-pagination-list justify-content-start"
                  style={{ flexWrap: "wrap", gap: "8px", alignItems: "flex-start" }}
                >
                  {/* Previous button */}
                  <li
                    className={page === 0 ? "disabled" : ""}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <a
                      href="#"
                      className="pagination-link animate-hover-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page === 0) return;

                        // Move one page back inside the current 5-page block.
                        // If we're at the first page in the block, jump to the last page of the previous block.
                        const blockStart = startPage;
                        let prevPage = page;

                        if (page > blockStart) {
                          prevPage = page - 1;
                        } else if (blockStart > 0) {
                          prevPage = blockStart - 1; // last page of previous block
                        }

                        if (prevPage < 0) return;

                        const next = new URLSearchParams(searchParams);
                        next.set("page", String(prevPage));
                        next.set("limit", String(limit));
                        setSearchParams(next);
                      }}
                    >
                      <span className="icon icon-arrow-left"></span>
                    </a>
                    <span style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                      {language === "ar" ? "السابق" : "Previous"}
                    </span>
                  </li>

                  {/* Page numbers 1-5 (or current range) - always show all 5 pages in current range */}
                  {pagesToShow.map((pageNum) => {
                    const pageIndex = pageNum - 1; // Convert to 0-based
                    const isActive = pageIndex === page;

                    return (
                      <li key={pageNum} className={isActive ? "active" : ""}>
                        <a
                          href="#"
                          className="pagination-link animate-hover-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            const next = new URLSearchParams(searchParams);
                            next.set("page", String(pageIndex));
                            next.set("limit", String(limit));
                            setSearchParams(next);
                          }}
                        >
                          {pageNum}
                        </a>
                      </li>
                    );
                  })}

                  {/* Next button: move one page forward until end of block, then jump to next 5-page block */}
                  <li
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <a
                      href="#"
                      className="pagination-link animate-hover-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        // Ensure page is a valid number
                        const currentPage = Number(page) || 0;

                        // Determine the current 5-page block (0-based indexes)
                        // If currentPage = 0-4  -> block 0 (pages 0-4, shown as 1-5)
                        // If currentPage = 5-9  -> block 1 (pages 5-9, shown as 6-10)
                        const currentBlockStart =
                          Math.floor(currentPage / 5) * 5;
                        const blockEndIndex = currentBlockStart + 4;

                        // Move one page forward
                        let nextPageIndex = currentPage + 1;

                        // If we've passed the end of this block, jump to the first page of the next block
                        if (nextPageIndex > blockEndIndex) {
                          nextPageIndex = currentBlockStart + 5;
                        }

                        const next = new URLSearchParams(searchParams);
                        next.set("page", String(nextPageIndex));
                        next.set("limit", String(limit));
                        setSearchParams(next);
                      }}
                      style={{
                        opacity: 1,
                        pointerEvents: "auto",
                        cursor: "pointer",
                      }}
                    >
                      <span className="icon icon-arrow-right"></span>
                    </a>
                    <span style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                      {language === "ar" ? "التالي" : "Next"}
                    </span>
                  </li>
                </ul>
              );
            })()}
          </div>
        </div>
      </section>

      {isFilterOpen && (
        <div
          className={`offcanvas-backdrop fade ${isFilterOpen ? "show" : ""}`}
          onClick={() => setIsFilterOpen(false)}
        />
      )}
      <div
        id="filterShop"
        className={`offcanvas offcanvas-start canvas-filter ${
          isFilterOpen ? "show" : ""
        }`}
        style={{ visibility: isFilterOpen ? "visible" : "hidden" }}
      >
        <div className="canvas-wrapper">
          <header className="canvas-header">
            <div className="filter-icon">
              <span className="icon icon-filter"></span>
              <span>{language === "ar" ? "المرشحات" : "Filter"}</span>
            </div>
            <span
              className="icon-close icon-close-popup"
              onClick={() => setIsFilterOpen(false)}
              aria-label={language === "ar" ? "إغلاق" : "Close"}
            ></span>
          </header>
          <div className="canvas-body">
            <form id="facet-filter-form" className="facet-filter-form">
              <div className="widget-facet">
                <div className="facet-title">
                  <span>{language === "ar" ? "التوفر" : "Availability"}</span>
                </div>
                <ul className="tf-filter-group mb_36">
                  {(language === "ar"
                    ? ["متوفر", "غير متوفر"]
                    : ["In stock", "Out of stock"]
                  ).map((v, index) => {
                    const englishValues = ["In stock", "Out of stock"];
                    const valueKey = englishValues[index];
                    return (
                      <li
                        key={valueKey}
                        className="list-item d-flex gap-12 align-items-center"
                      >
                        <input
                          type="radio"
                          name="availability"
                          className="tf-check"
                          id={`avail-${valueKey}`}
                          checked={filters.availability === valueKey}
                          onChange={() => setFilter("availability", valueKey)}
                        />
                        <label htmlFor={`avail-${valueKey}`} className="label">
                          <span>{v}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="widget-facet">
                <div className="facet-title">
                  <span>
                    {language === "ar" ? "العلامة التجارية" : "Brand"}
                  </span>
                </div>
                <ul className="tf-filter-group mb_36">
                  {["Elsallab", "Ecomus", "M&H"].map((v) => (
                    <li
                      key={v}
                      className="list-item d-flex gap-12 align-items-center"
                    >
                      <input
                        type="radio"
                        name="brand"
                        className="tf-check"
                        id={`brand-${v}`}
                        checked={filters.brand === v}
                        onChange={() => setFilter("brand", v)}
                      />
                      <label htmlFor={`brand-${v}`} className="label">
                        <span>{v}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="widget-facet">
                <div className="facet-title">
                  <span>{language === "ar" ? "اللون" : "Color"}</span>
                </div>
                <ul className="tf-filter-group filter-color mb_36">
                  {["White", "Black", "Blue"].map((v) => (
                    <li
                      key={v}
                      className="list-item d-flex gap-12 align-items-center"
                    >
                      <input
                        type="radio"
                        name="color"
                        className="tf-check-color"
                        id={`color-${v}`}
                        checked={filters.color === v}
                        onChange={() => setFilter("color", v)}
                      />
                      <label htmlFor={`color-${v}`} className="label">
                        <span>{v}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="widget-facet">
                <div className="facet-title">
                  <span>{language === "ar" ? "المقاس" : "Size"}</span>
                </div>
                <ul className="tf-filter-group">
                  {["S", "M", "L", "XL"].map((v) => (
                    <li
                      key={v}
                      className="list-item d-flex gap-12 align-items-center"
                    >
                      <input
                        type="radio"
                        name="size"
                        className="tf-check tf-check-size"
                        id={`size-${v}`}
                        checked={filters.size === v}
                        onChange={() => setFilter("size", v)}
                      />
                      <label htmlFor={`size-${v}`} className="label">
                        <span>{v}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              {appliedFilters.length > 0 && (
                <button
                  className="tf-btn btn-sm btn-fill w-100"
                  onClick={removeAllFilters}
                >
                  {language === "ar" ? "مسح الكل" : "Clear all"}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ShopDefaultPage;
